'use client';
import { Country, Province } from '@/api/types/public';
import { countryValidator, getErrorTranslationKey } from '@/utils/form-rule';
import { parsePhoneValue } from '@/utils/tools';
import { Box } from '@mui/material';
import { RightArrowFineOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AddressFormData } from '.';
import AutoComplete from '../Form/AutoComplete';
import Input, { type FloatingLabelVariant } from '../Form/FormInput';
import WapOnly from '../Responsive/WapOnly';
import WebOnly from '../Responsive/WebOnly';
import CountryDrawer from './MobileCountryDrawer';

export default function FormItemCountry({
  formData,
  countryList,
  setProvinceList,
  telCodeSelected,
  disabled,
  floatingLabelVariant,
  formItemClassName,
  overlayZIndex,
}: {
  formData: UseFormReturn<AddressFormData, any, AddressFormData>;
  countryList: Country[];
  setProvinceList: (provinceList: Province[]) => void;
  telCodeSelected: boolean;
  disabled?: boolean;
  floatingLabelVariant?: FloatingLabelVariant;
  formItemClassName?: string;
  overlayZIndex?: number;
}) {
  const t = useTranslations('address');
  const tValidation = useTranslations('components.address.validation');
  const [showCountryDrawer, setShowCountryDrawer] = useState(false);
  const handleOnCountrySelect = (_country: Country) => {
    if (_country.country_id !== formData.getValues('country_id')) {
      setProvinceList(_country.province || []);
      formData.setValue('country', _country.country);
      formData.setValue('country_id', _country.country_id);
      formData.setValue('cpf', '');
      formData.setValue('province_id', '');
      formData.setValue('province', '');
      formData.setValue('region_code', '');
      const { telephone } = parsePhoneValue(formData.getValues('telephone'));
      if (!telCodeSelected && !telephone) {
        formData.setValue('telCode', `${_country.country_id}(+${_country.tel_code})`);
      }
    }
  };
  return (
    <>
      <WapOnly>
        <CountryDrawer
          show={showCountryDrawer}
          zIndex={overlayZIndex}
          close={() => {
            setShowCountryDrawer(false);
          }}
          onSelect={(country) => {
            handleOnCountrySelect(country);
          }}
        />
      </WapOnly>
      <Box sx={{ width: '100%' }}>
        <input name="state" hidden />
        <input name="country" hidden />
        <WebOnly>
          <AutoComplete
            name="country"
            label={disabled ? '' : t('countryRegion')}
            required
            formItemClassName={formItemClassName}
            floatingLabelVariant={floatingLabelVariant}
            popperZIndex={overlayZIndex}
            disabled={disabled}
            multiple={false}
            control={formData.control}
            options={countryList.map((item) => item.country)}
            disableClearable
            onChange={(_, value) => {
              handleOnCountrySelect(countryList.find((item) => item.country === value));
            }}
            rules={{
              validate: () => {
                const errorCode = countryValidator(formData.getValues('country'));
                if (errorCode) {
                  const translationKey = getErrorTranslationKey(errorCode);
                  return translationKey ? tValidation(translationKey as any) : 'Validation error';
                }
                return true;
              },
            }}
          />
        </WebOnly>
        <WapOnly>
          <Input
            name="country"
            label={disabled ? '' : t('countryRegion')}
            required
            formItemClassName={formItemClassName}
            floatingLabelVariant={floatingLabelVariant}
            disabled={disabled}
            control={formData.control}
            onClick={() => {
              !disabled && setShowCountryDrawer(true);
            }}
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
            rules={{
              validate: () => {
                const errorCode = countryValidator(formData.getValues('country'));
                if (errorCode) {
                  const translationKey = getErrorTranslationKey(errorCode);
                  return translationKey ? tValidation(translationKey as any) : 'Validation error';
                }
                return true;
              },
            }}
            endIcon={
              disabled ? null : (
                <RightArrowFineOutlined
                  className="text-[16px] text-[#6c6c6c] [&_path]:stroke-[#6c6c6c]"
                  style={{ color: '#6C6C6C', fill: 'none' }}
                />
              )
            }
          />
        </WapOnly>
      </Box>
    </>
  );
}
